import { Pencil, Trash2, Upload, X, Download, FileText, Image, File } from 'lucide-react';
import type { CatalogItem, CatalogFile } from '../../types/catalog';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  EmptyState,
} from '../ui';

interface CatalogListProps {
  items: CatalogItem[];
  onEdit: (item: CatalogItem) => void;
  onDelete: (item: CatalogItem) => void;
  onUpload: (item: CatalogItem) => void;
  onDeleteFile: (itemId: number, fileId: number, fileCount: number) => void;
}

const FILE_TYPE_ICONS = {
  pdf: <FileText className="h-8 w-8 text-red-500" />,
  jpg: <Image className="h-8 w-8 text-blue-500" />,
  jpeg: <Image className="h-8 w-8 text-blue-500" />,
  png: <Image className="h-8 w-8 text-green-500" />,
};

function CatalogList({ items, onEdit, onDelete, onUpload, onDeleteFile }: CatalogListProps) {
  const getFileIcon = (fileName: string | null) => {
    if (!fileName) return <File className="h-8 w-8" />;
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) return <File className="h-8 w-8" />;
    return (
      FILE_TYPE_ICONS[extension as keyof typeof FILE_TYPE_ICONS] || <File className="h-8 w-8" />
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName: string | null, maxLength: number = 30) => {
    if (!fileName) return '';
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  };

  const renderFileList = (files: CatalogFile[] | null, itemId: number, showDelete = true) => {
    if (!files || !Array.isArray(files) || files.length === 0) return null;

    return (
      <div className="space-y-3">
        {files.map(file => (
          <div
            key={file.id}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              {getFileIcon(file.file_name)}
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {truncateFileName(file.file_name)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.file_size)}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.open(file.file_url, '_blank')}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </Button>
              {showDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteFile(itemId, file.id, files.length)}
                  title="Excluir arquivo"
                >
                  <X className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map(item => (
        <div
          key={item.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{item.title}</h3>
              <Badge variant="secondary" className="mt-1">
                {item.type}
              </Badge>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => onUpload(item)}
                title="Fazer Upload"
              >
                <Upload className="h-4 w-4 mr-1" />
                Upload
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(item)} title="Editar">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(item)} title="Excluir">
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          </div>

          {item.summary && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{item.summary}</p>
          )}

          {!item.files || !Array.isArray(item.files) || item.files.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Nenhum arquivo anexado</p>
              <Button variant="link" onClick={() => onUpload(item)} className="mt-2">
                Fazer upload
              </Button>
            </div>
          ) : (
            renderFileList(item.files, item.id)
          )}
        </div>
      ))}
    </div>
  );
}

export default CatalogList;
